// Local-first IndexedDB layer. Source of truth for all user data.
//
// Design:
// - Every row gets a `local_uuid` (string) generated on insert; this is
//   the stable primary key in IndexedDB.
// - `server_id` (number|null) is null until the row has been uploaded to
//   the server. After upload, it's set to the server's auto-increment id.
// - `updated_at` (ISO string) is bumped on every local write. The sync
//   engine uses it to decide what's dirty (needs pushing) and for
//   last-write-wins conflict resolution.
// - `deleted` (boolean) for soft-delete so a delete syncs properly as
//   a tombstone instead of disappearing from the local DB before the
//   server has acknowledged.
//
// Schema versioning is via Dexie's `version(N).stores({...})` chain.
// When the schema changes, bump the version and add a migration.

import Dexie from 'dexie';
import { DEFAULT_ACCOUNT_ID } from './accounts.js';


    // Phase 59: add accounts table.
    //   - Each transaction belongs to an account via account_local_uuid.
    //   - Default account '現金' seeded on first run.
    //   - Existing transactions (pre-migration) get default-account backfill
    //     via the .upgrade() callback below.
    // (Moved into FinanceDB class below — declared here as a comment to keep history)

    const DEFAULT_CATEGORIES = [
  // Expense (sort_order: 1-99). Phase 79f: each seed row carries
  // is_default=true so the Settings UI can distinguish built-in
  // categories from user-created ones. User toggles via the
  // `disabled` flag (hidden from pickers without losing the row).
  { name: '餐飲', type: 'expense', icon: '🍔', color: '#ef4444', sort_order: 1, is_default: true },
  { name: '交通', type: 'expense', icon: '🚇', color: '#3b82f6', sort_order: 2, is_default: true },
  { name: '水費', type: 'expense', icon: '💧', color: '#06b6d4', sort_order: 3, is_default: true },
  { name: '電費', type: 'expense', icon: '⚡', color: '#eab308', sort_order: 4, is_default: true },
  { name: '煤氣', type: 'expense', icon: '🔥', color: '#f97316', sort_order: 5, is_default: true },
  { name: '居家', type: 'expense', icon: '🏠', color: '#8b5cf6', sort_order: 6, is_default: true },
  { name: '投資', type: 'expense', icon: '📈', color: '#6366f1', sort_order: 7, is_default: true },
  { name: '娛樂', type: 'expense', icon: '🎬', color: '#ec4899', sort_order: 8, is_default: true },
  { name: '購物', type: 'expense', icon: '🛍️', color: '#f43f5e', sort_order: 9, is_default: true },
  { name: '醫療', type: 'expense', icon: '💊', color: '#14b8a6', sort_order: 10, is_default: true },
  { name: '稅務', type: 'expense', icon: '🧾', color: '#f59e0b', sort_order: 11, is_default: true },
  // Phase 82b: 卡數 — paying the credit card bill. Distinct from credit
  // card purchases (which use the credit card payment method).
  { name: '卡數', type: 'expense', icon: '💳', color: '#0ea5e9', sort_order: 12, is_default: true },
  { name: '其他', type: 'expense', icon: '❓', color: '#64748b', sort_order: 99, is_default: true },
  // Income
  { name: '薪金', type: 'income', icon: '💰', color: '#10b981', sort_order: 1, is_default: true },
  { name: '投資回報', type: 'income', icon: '📈', color: '#6366f1', sort_order: 2, is_default: true },
  { name: '其他', type: 'income', icon: '💵', color: '#64748b', sort_order: 99, is_default: true },
  { name: '獎金', type: 'income', icon: '🎁', color: '#f59e0b', sort_order: 3, is_default: true },
];

const DEFAULT_SETTINGS = {
  currency: '$',
  locale: 'zh-HK',
  currency_symbols: '["$","€","£","¥","₩"]',
  notification_on_grace: 'false',
  // Phase 208 (card 208-B4): the GLOBAL opt-in for recurring-transaction
  // reminders. Seeded explicitly as 'false' — the feature is off until
  // the user turns it on, even if a rule has reminder_enabled = 1.
  // Installs that pre-date the key have no row at all; the read helper
  // (frontend/src/lib/recurringReminderSetting.js) treats an absent key
  // as OFF, so both paths agree.
  recurring_reminder_enabled: 'false',
  // Phase 36: interface language. Falls back to 'zh-Hant' (matches the
  // original UI baseline) for users on older DBs that pre-date this key.
  language: 'zh-Hant',
};

// Generate a UUIDv4. crypto.randomUUID is supported in all modern browsers.
function uuid() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
}

// Monotonic timestamp generator.
//
// `updated_at` is the local write clock. Two problems with a plain
// `new Date().toISOString()`:
//
//   1. Same-millisecond collisions. A create and a follow-up update
//      within the same millisecond produce an IDENTICAL updated_at.
//      Sync conflict resolution compares updated_at with strict `>`
//      (see syncDirty / the server's "newer than the server's copy"
//      rule), so a same-millisecond update can be treated as "not
//      newer" and silently never push.
//   2. Wall-clock regression. If the system clock steps backwards, a
//      later write gets an EARLIER timestamp and is likewise lost.
//
// Both are real (a ~40% flake rate was observed in tests/localdb.test.js
// on the "updated_at bumped" assertion). Fix: keep the last value and
// guarantee each call returns a strictly greater timestamp, nudging by
// 1ms when the clock has not advanced.
//
// Format is unchanged (ISO 8601 with milliseconds, UTC) so existing
// parsers and the `^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$`
// assertions still hold.
let _lastNowMs = 0;
function nowIso() {
  const wall = Date.now();
  const ms = wall > _lastNowMs ? wall : _lastNowMs + 1;
  _lastNowMs = ms;
  return new Date(ms).toISOString();
}

class FinanceDB extends Dexie {
  constructor() {
    super('finance-app-local');
    // Version 1: initial schema. Indexes are the columns we'll filter
    // or sort by frequently. Primary key is local_uuid.
    this.version(1).stores({
      // [primaryKey, ...indexes]
      transactions: 'local_uuid, server_id, type, occurred_on, category_id, created_at, updated_at, deleted',
      categories:   'local_uuid, server_id, type, sort_order, deleted',
      settings:     'local_uuid, key, deleted',
      budgets:      'local_uuid, server_id, category_id, deleted',
      recurring:    'local_uuid, server_id, active, deleted',
      credit_cards: 'local_uuid, server_id, deleted',
      // Track sync metadata — single row, primary key = 'singleton'
      _meta:        'key',
    });
    // Phase 48: Savings goals. Linked to a category for auto-tracking
    // (sum of transactions in that category since `tracking_from`), or
    // manual mode when `linked_category_id` is null and user types
    // `current_override`. `current_amount` in the row is recomputed
    // from transactions on read (see computeGoalProgress in lib/goals.js).
    // Phase 48d: Savings goals. IndexedDB schema mirrors the server
    // `goals` table. v3 adds `allocation_pct` (per-goal share of the
    // user's monthly net) and `active` (toggle to pause a goal without
    // deleting it). Phase 48d logic: multiple ACTIVE goals share monthly
    // net according to allocation_pct (sum across active = 100).
    this.version(3).stores({
      transactions: 'local_uuid, server_id, type, occurred_on, category_id, created_at, updated_at, deleted',
      categories:   'local_uuid, server_id, type, sort_order, deleted',
      settings:     'local_uuid, key, deleted',
      budgets:      'local_uuid, server_id, category_id, deleted',
      recurring:    'local_uuid, server_id, active, deleted',
      credit_cards: 'local_uuid, server_id, deleted',
      goals:        'local_uuid, server_id, linked_category_id, active, deleted',
      _meta:        'key',
    });
    // v2 schema kept for migration safety: users who installed the
    // v2 schema get auto-upgraded by Dexie; the v3 block above
    // supersedes it.
    this.version(2).stores({
      transactions: 'local_uuid, server_id, type, occurred_on, category_id, created_at, updated_at, deleted',
      categories:   'local_uuid, server_id, type, sort_order, deleted',
      settings:     'local_uuid, key, deleted',
      budgets:      'local_uuid, server_id, category_id, deleted',
      recurring:    'local_uuid, server_id, active, deleted',
      credit_cards: 'local_uuid, server_id, deleted',
      goals:        'local_uuid, server_id, linked_category_id, deleted',
      _meta:        'key',
    });
    // Phase 59: add accounts + extend transactions index with
    //   account_local_uuid. .upgrade() backfills existing rows.
    this.version(4).stores({
      accounts:     'local_uuid, server_id, name, type, sort_order, deleted',
      transactions: 'local_uuid, server_id, type, occurred_on, category_id, account_local_uuid, created_at, updated_at, deleted',
      categories:   'local_uuid, server_id, type, sort_order, deleted',
      settings:     'local_uuid, key, deleted',
      budgets:      'local_uuid, server_id, category_id, deleted',
      recurring:    'local_uuid, server_id, active, deleted',
      credit_cards: 'local_uuid, server_id, deleted',
      goals:        'local_uuid, server_id, linked_category_id, active, deleted',
      _meta:        'key',
    }).upgrade(async (tx) => {
      const DEFAULT_ACCT_ID = 'default-cash-account';
      await tx.table('accounts').put({
        local_uuid: DEFAULT_ACCT_ID, server_id: null,
        name: '現金', type: 'cash', icon: '💵', color: '#10b981',
        sort_order: 1, deleted: false,
        updated_at: new Date().toISOString(), created_at: new Date().toISOString(),
      });
      const txs = await tx.table('transactions').toArray();
      for (const row of txs) {
        if (!row.account_local_uuid) {
          row.account_local_uuid = DEFAULT_ACCT_ID;
          await tx.table('transactions').put(row);
        }
      }
    });

    // Phase 60: add transfer_group_id to transactions. When user moves money
    //   from account A to B, we create 2 tx rows (1 expense on A, 1 income
    //   on B) sharing the same transfer_group_id. The Transactions page can
    //   filter by transfer_group_id to show "only transfers".
    this.version(5).stores({
      accounts:     'local_uuid, server_id, name, type, sort_order, deleted',
      transactions: 'local_uuid, server_id, type, occurred_on, category_id, account_local_uuid, transfer_group_id, created_at, updated_at, deleted',
      categories:   'local_uuid, server_id, type, sort_order, deleted',
      settings:     'local_uuid, key, deleted',
      budgets:      'local_uuid, server_id, category_id, deleted',
      recurring:    'local_uuid, server_id, active, deleted',
      credit_cards: 'local_uuid, server_id, deleted',
      goals:        'local_uuid, server_id, linked_category_id, active, deleted',
      _meta:        'key',
    });
    // Phase 67: Credit card installment plans.
    // A `total_amount` is split across `total_periods` monthly payments
    // starting `start_month` (YYYY-MM). Indexed by credit_card_local_uuid
    // for fast lookup, started/ended for time-range filtering.
    this.version(6).stores({
      accounts:     'local_uuid, server_id, name, type, sort_order, deleted',
      transactions: 'local_uuid, server_id, type, occurred_on, category_id, account_local_uuid, transfer_group_id, created_at, updated_at, deleted',
      categories:   'local_uuid, server_id, type, sort_order, deleted',
      settings:     'local_uuid, key, deleted',
      budgets:      'local_uuid, server_id, category_id, deleted',
      recurring:    'local_uuid, server_id, active, deleted',
      credit_cards: 'local_uuid, server_id, deleted',
      goals:        'local_uuid, server_id, linked_category_id, active, deleted',
      installments: 'local_uuid, credit_card_local_uuid, started, ended, deleted',
      _meta:        'key',
    });
    // Phase 102: user preferences (onboarding state, replayable tutorial).
    // Single-row-per-key, mirroring `_meta`. New table is purely additive
    // — existing v6 users auto-upgrade without row migration.
    this.version(7).stores({
      accounts:     'local_uuid, server_id, name, type, sort_order, deleted',
      transactions: 'local_uuid, server_id, type, occurred_on, category_id, account_local_uuid, transfer_group_id, created_at, updated_at, deleted',
      categories:   'local_uuid, server_id, type, sort_order, deleted',
      settings:     'local_uuid, key, deleted',
      budgets:      'local_uuid, server_id, category_id, deleted',
      recurring:    'local_uuid, server_id, active, deleted',
      credit_cards: 'local_uuid, server_id, deleted',
      goals:        'local_uuid, server_id, linked_category_id, active, deleted',
      installments: 'local_uuid, credit_card_local_uuid, started, ended, deleted',
      user_prefs:   'key',
      _meta:        'key',
    });
    // Phase 109b: recurring_audit tracks which (recurring, year_month)
    // pairs have already been auto-applied to transactions. Without it,
    // every dashboard mount would re-insert the same monthly tx.
    // Schema mirrors server's recurring_audit table, but uses
    // recurring_local_uuid instead of recurring_id. Compound primary key
    // [recurring_local_uuid+year_month] gives O(1) duplicate check.
    this.version(8).stores({
      accounts:         'local_uuid, server_id, name, type, sort_order, deleted',
      transactions:     'local_uuid, server_id, type, occurred_on, category_id, account_local_uuid, transfer_group_id, created_at, updated_at, deleted',
      categories:       'local_uuid, server_id, type, sort_order, deleted',
      settings:         'local_uuid, key, deleted',
      budgets:          'local_uuid, server_id, category_id, deleted',
      recurring:        'local_uuid, server_id, active, deleted',
      recurring_audit:  '[recurring_local_uuid+year_month], recurring_local_uuid, year_month',
      credit_cards:     'local_uuid, server_id, deleted',
      goals:            'local_uuid, server_id, linked_category_id, active, deleted',
      installments:     'local_uuid, credit_card_local_uuid, started, ended, deleted',
      user_prefs:       'key',
      _meta:            'key',
    });
    // Phase 35e (sync retry queue): durable push-retry queue. When a
    // background push fails (network error, 5xx, 408, 429), we enqueue
    // the request here. `flushSyncQueue` walks pending entries on app
    // boot + on reconnect. Successful pushes are removed; transient
    // failures bump `attempts`; permanent failures (4xx other than
    // 408/429) flip status to 'failed' so they stop retrying.
    //
    // Schema is purely additive — v8 users upgrade automatically with
    // an empty queue.
    this.version(9).stores({
      accounts:         'local_uuid, server_id, name, type, sort_order, deleted',
      transactions:     'local_uuid, server_id, type, occurred_on, category_id, account_local_uuid, transfer_group_id, created_at, updated_at, deleted',
      categories:       'local_uuid, server_id, type, sort_order, deleted',
      settings:         'local_uuid, key, deleted',
      budgets:          'local_uuid, server_id, category_id, deleted',
      recurring:        'local_uuid, server_id, active, deleted',
      recurring_audit:  '[recurring_local_uuid+year_month], recurring_local_uuid, year_month',
      credit_cards:     'local_uuid, server_id, deleted',
      goals:            'local_uuid, server_id, linked_category_id, active, deleted',
      installments:     'local_uuid, credit_card_local_uuid, started, ended, deleted',
      user_prefs:       'key',
      push_queue:       'local_uuid, table, status, attempts, created_at',
      _meta:            'key',
    });
  }
}

export const db = new FinanceDB();

// Phase 152 (M-79): test escape hatch — when globalThis.__financeAppTestDb__
// is set, return a Proxy that delegates .table() to a per-table mock
// from the harness. Production code never sets the key, so the real
// Dexie instance is used. Mock surface: { table: (name) => { toArray,
// put, update, get, delete, add, ... } } per table name.
function pickDb() {
  return (typeof globalThis !== 'undefined' && globalThis.__financeAppTestDb__) || db;
}
export const dbMockable = new Proxy({}, {
  get(_t, key) {
    const target = pickDb();
    const v = target[key];
    return typeof v === 'function' ? v.bind(target) : v;
  },
  has(_t, key) { return key in pickDb(); },
});

// Phase 155 (M-82): per-repo test escape hatches for txRepo/categoryRepo/
// budgetRepo/recurringRepo/cardRepo/installmentsRepo/goalRepo. Same
// pattern as dataService.data Proxy (M-76) — production code is unchanged
// (zero perf impact when the global key is unset), tests inject a
// per-repo mock via the harness.
function pickRepo(name) {
  return (typeof globalThis !== 'undefined' && globalThis[`__financeAppTest${name}__`]);
}

// Phase 155 (M-82): per-repo mockable wrappers. When a test sets
// globalThis.__financeAppTestTxRepo__ (etc), the wrapper delegates
// to that mock object. Otherwise it delegates to the real txRepo
// (which still hits Dexie). Production code in sampleData.js uses
// these wrappers so that tests can inject per-repo mocks without
// touching Dexie. Real production callers (Dashboard, transactions
// page, etc.) keep importing `txRepo` directly and stay unchanged.
function makeRepoProxy(repoName, fallback) {
  return new Proxy({}, {
    get(_t, key) {
      const target = pickRepo(repoName) || fallback;
      const v = target[key];
      return typeof v === 'function' ? v.bind(target) : v;
    },
    has(_t, key) {
      const target = pickRepo(repoName) || fallback;
      return key in target;
    },
  });
}

// Phase 168 (M-95): createCrudRepo — shared scaffold for repos whose
// only job is create / update / soft-delete on a single Dexie table.
// The 8 repos in this file share ~3 lines of identical CRUD plumbing:
//   - add(input): build row (uuid + server_id:null + deleted:false +
//     updated_at:nowIso() + repo-specific defaults), db.add, return row
//   - update(local_uuid, patch): db.update with updated_at:nowIso(),
//     return db.get(local_uuid)
//   - remove(local_uuid): soft-delete (deleted:true + updated_at)
// Repos with specialised behaviour (txRepo sync push, recurringRepo
// cache clear, budgetRepo where-by-category_id) extend this base.
//
// @param {string} tableName  Dexie table name (e.g. 'credit_cards')
// @param {object} defaults   extra fields merged into every `add` row
//                            (e.g. { ended: false, created_at: nowIso() })
// @returns {object}          { add, update, remove } — caller spreads
//                            and adds specialised methods like `list`.
function createCrudRepo(tableName, defaults = {}) {
  return {
    async add(input) {
      const row = {
        local_uuid: uuid(),
        server_id: null,
        deleted: false,
        updated_at: nowIso(),
        ...defaults,
        ...input,
      };
      await db[tableName].add(row);
      return row;
    },
    async update(local_uuid, patch) {
      await db[tableName].update(local_uuid, { ...patch, updated_at: nowIso() });
      return db[tableName].get(local_uuid);
    },
    async remove(local_uuid) {
      await db[tableName].update(local_uuid, { deleted: true, updated_at: nowIso() });
    },
  };
}

// ─── Bootstrap ──────────────────────────────────────────────────────────
// Called once on app boot. Seeds the sentinel categories + settings
// if the DB is empty. Idempotent — safe to call on every load.

// Phase 79f: ensureSeeded can be invoked concurrently (React StrictMode
// double-invokes effects in dev, and auth.jsx also calls it from
// initialization). Without this singleton, two parallel calls both see
// `catCount === 0` and race to bulkAdd 16 rows → 32 rows total.
// Module-level promise: the second caller awaits the first one's
// completion instead of running the seed logic again.
let _ensureSeededInFlight = null;
export async function ensureSeeded() {
  if (_ensureSeededInFlight) return _ensureSeededInFlight;
  _ensureSeededInFlight = (async () => {
    const catCount = await db.categories.count();
    if (catCount === 0) {
      await db.categories.bulkAdd(
        DEFAULT_CATEGORIES.map(c => ({
          local_uuid: uuid(),
          server_id: null,
          deleted: false,
          updated_at: nowIso(),
          ...c,
        }))
      );
    } else {
    // One-shot migration for older installs that pre-date the 稅務
    // category. Idempotent — only inserts if no row with this name+type
    // already exists, so re-running on a freshly-seeded DB is a no-op.
    const taxDef = DEFAULT_CATEGORIES.find(c => c.name === '稅務');
    const hasTax = await db.categories.filter(c => c.name === '稅務' && c.type === 'expense').first();
    if (taxDef && !hasTax) {
      await db.categories.add({
        local_uuid: uuid(),
        server_id: null,
        deleted: false,
        updated_at: nowIso(),
        ...taxDef,
      });
    }
    // Phase 82b: 卡數 — paying credit card bill. Migration also handles
    // the rename from the earlier 交卡數 label shipped in Phase 82: if a
    // row with the old name exists, rename it to the new name rather
    // than inserting a duplicate. New users with no row get the standard
    // insert path.
    const ccPayDef = DEFAULT_CATEGORIES.find(c => c.name === '卡數');
    const oldCcPay = await db.categories.filter(c => c.name === '交卡數' && c.type === 'expense').first();
    const newCcPay = await db.categories.filter(c => c.name === '卡數' && c.type === 'expense').first();
    if (oldCcPay && !newCcPay) {
      // Rename old row in place; preserve local_uuid/server_id/is_default.
      await db.categories.update(oldCcPay.local_uuid, { name: '卡數' });
    } else if (ccPayDef && !newCcPay && !oldCcPay) {
      await db.categories.add({
        local_uuid: uuid(),
        server_id: null,
        deleted: false,
        updated_at: nowIso(),
        ...ccPayDef,
      });
    }
    // Phase 79f: backfill is_default + disabled on existing local rows
    // that pre-date this field. Rules:
    //   - For each (name, type) group, only mark the FIRST row (by
    //     local_uuid lexicographic order) as default. This is the row
    //     that the seeding code inserted. User-added duplicates of
    //     the same name keep is_default=false so they remain editable.
    //   - For any (name, type) NOT in DEFAULT_CATEGORIES, is_default=0.
    //   - All rows get disabled=0 unless the user already set it.
    // Idempotent — second run is a no-op because rows already carry
    // the values from the first run.
    const defaultLookup = new Set(
      DEFAULT_CATEGORIES.map(c => `${c.name}\u0000${c.type}`)
    );
    const allCats = await db.categories.toArray();
    // Group by (name, type), then mark only the first row of each
    // matching group as default. Sort each group by local_uuid for
    // stable ordering across runs.
    const groups = new Map();
    for (const c of allCats) {
      const key = `${c.name}\u0000${c.type}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    }
    for (const [key, rows] of groups) {
      const isSeedGroup = defaultLookup.has(key);
      // Stable order: local_uuid lexicographic.
      rows.sort((a, b) => (a.local_uuid < b.local_uuid ? -1 : 1));
      rows.forEach((c, idx) => {
        const patch = {};
        if (c.is_default === undefined) {
          patch.is_default = isSeedGroup && idx === 0 ? 1 : 0;
        }
        if (c.disabled === undefined) {
          patch.disabled = 0;
        }
        if (Object.keys(patch).length > 0) {
          // Synchronous update — we're in the same async fn but
          // db.categories.update returns a promise; fire-and-forget
          // updates inside a loop are fine here because each is
          // independent (no transaction needed).
          db.categories.update(c.local_uuid, { ...patch, updated_at: nowIso() });
        }
      });
    }
    // Wait for all queued updates to settle before continuing.
    await new Promise(r => setTimeout(r, 0));
  }
  const settingsCount = await db.settings.count();
  if (settingsCount === 0) {
    await db.settings.bulkAdd(
      Object.entries(DEFAULT_SETTINGS).map(([key, value]) => ({
        local_uuid: uuid(),
        server_id: null,
        key,
        value,
        deleted: false,
        updated_at: nowIso(),
      }))
    );
  }
  // Phase 203 (bug fix): seed the default cash account.
  //
  // The default account was ONLY ever created by the v3→v4 Dexie
  // `.upgrade()` callback (see the version(4) block above). A FRESH
  // install opens the DB at the latest version and never runs that
  // callback — so a new user's `accounts` table was EMPTY. That made
  // the account picker show no accounts, the transfer dropdowns empty,
  // and Settings → Accounts show the empty state on every new device.
  //
  // Seeding it here (idempotently, alongside the categories + settings)
  // makes fresh installs match upgraded ones.
  const acctCount = await db.accounts.count();
  if (acctCount === 0) {
    await db.accounts.put({
      local_uuid: DEFAULT_ACCOUNT_ID, server_id: null,
      name: '現金', type: 'cash', icon: '💵', color: '#10b981',
      sort_order: 1, deleted: false,
      updated_at: nowIso(), created_at: nowIso(),
    });
  }
  // Meta defaults
  const metaCount = await db._meta.count();
  if (metaCount === 0) {
    await db._meta.bulkAdd([
      { key: 'local_user_uuid', value: uuid() },
      { key: 'connected_google_sub', value: null },
      { key: 'connected_user_email', value: null },
      { key: 'last_pulled_at', value: null },
      { key: 'last_pushed_at', value: null },
      { key: 'last_sync_status', value: 'idle' }, // 'idle' | 'syncing' | 'error' | 'offline'
    ]);

    // Phase 102: seed user_prefs.onboarding with default row. On first
    // ever boot we write the row so App.jsx has a guaranteed place to
    // mark completed/dismissed. Both timestamps stay null = "show the
    // carousel once".
    await db.user_prefs.put({
      key: 'onboarding',
      value: {
        completed_at: null,
        dismissed_at: null,
        seen_step: 0,
      },
      updated_at: new Date().toISOString(),
    });

    // Phase 97 (B-01 fix): the two this.version(5).stores(...) and
    //   this.version(6).stores(...) blocks that used to live here have
    //   been deleted. ensureSeeded is an async FUNCTION, not a METHOD —
    //   when called as `await ensureSeeded()`, `this` is undefined and
    //   `this.version(N)` threw `Cannot read properties of undefined`.
    //   The Dexie `Ht` constructor above already declares v1 → v6, so
    //   these duplicate declarations were always dead. Migration on
    //   upgrade from v4/v5 was silently broken (caught by React's
    //   try/catch, so the app continued but the schema wasn't migrated).
    //   Now nothing in this branch throws — `_meta` rows seed cleanly.
  }
  // End of IIFE inside ensureSeeded (Phase 79f singleton guard).
  })();
  // Phase 112 (M-39): return the in-flight promise so callers can await
  //   the seed actually completing. Without this, `await ensureSeeded()`
  //   resolved with `undefined` immediately and the caller's next read
  //   (e.g. `categoryRepo.list()`) raced against the seed IIFE — most
  //   of the time the read came back empty, surfacing as
  //   "Cannot read properties of undefined (reading 'local_uuid')" in
  //   11 tests. The promise is also cached in `_ensureSeededInFlight`,
  //   so subsequent calls correctly await the same work.
  return _ensureSeededInFlight;
}

// ─── Meta helpers ──────────────────────────────────────────────────────

export async function getMeta(key) {
  const row = await db._meta.get(key);
  return row?.value ?? null;
}
export async function setMeta(key, value) {
  await db._meta.put({ key, value });
}

// ─── Phase 46: wipe the entire local account ───────────────────────────
//
// Settings → Account → "Delete local account" invokes this. After it
// returns the app reloads and the user lands back in a fresh
// local-user state. Safe to call while connected to Google — the
// server-side data isn't touched, just the local mirror.
//
// We close the DB first so Dexie releases its connection; otherwise
// the in-memory schema cache stays warm and the next page boot
// receives a stale "tables exist" view.
export async function wipeLocalAccount() {
  try { db.close(); } catch {}
  // Delete the IndexedDB database outright — this drops every store
  // including _meta, so all subsequent state (local_user_uuid,
  // connected_google_sub, last sync state) is gone.
  await new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(db.name);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
    // Fake-indexeddb fires `onblocked` if any tab holds the DB open;
    // ignore that path (no real-world browser tabs share the DB).
    req.onblocked = () => resolve();
  });
  // Re-open the DB so the next page boot can re-seed cleanly.
  await db.open();
  // Clear service-worker notification permission cache so a fresh
  // local user isn't auto-prompted with the old permission state.
  try { localStorage.removeItem('finance-app:notifications'); } catch {}
}

// ─── Transactions ───────────────────────────────────────────────────────

export const txRepo = {
  async list({ from, to, type, category_id, limit } = {}) {
    // Read all + filter + sort in JS. Transaction lists are at most
    // thousands of rows for a personal app; full-table scan is fine.
    const all = await db.transactions.toArray();
    let arr = all.filter(t => !t.deleted);
    if (from) arr = arr.filter(t => t.occurred_on >= from);
    if (to)   arr = arr.filter(t => t.occurred_on <= to);
    if (type) arr = arr.filter(t => t.type === type);
    if (category_id) arr = arr.filter(t => t.category_id === category_id);
    // Sort newest first by occurred_on, then created_at as tiebreaker
    arr.sort((a, b) => {
      if (a.occurred_on !== b.occurred_on) return a.occurred_on < b.occurred_on ? 1 : -1;
      return (a.created_at || '') < (b.created_at || '') ? 1 : -1;
    });
    if (limit && Number.isInteger(limit)) arr = arr.slice(0, limit);
    return arr;
  },
  // Phase 106 (M-36): cheap count of non-deleted rows. Used by the
  // first-tx celebration guard in Add.jsx. Dexie's `db.transactions.count()`
  // already excludes soft-deleted rows? No — soft-delete is a column
  // value, not a Dexie deletion. So we count via Dexie and then
  // subtract deleted=1. For very large tables this becomes slow, but
  // a personal-finance app rarely exceeds a few thousand rows.
  async count() {
    const total = await db.transactions.count();
    if (total === 0) return 0;
    const deleted = await db.transactions.where('deleted').equals(1).count();
    return total - deleted;
  },
  async get(local_uuid) {
    return db.transactions.get(local_uuid);
  },
  // Phase 169 (M-96): CRUD via createCrudRepo factory. created_at:nowIso()
  // is a tx-specific default. The specialised list/count/bulkRemove/wipe
  // methods below stay in this object.
  ...createCrudRepo('transactions', { created_at: nowIso() }),
  // Phase 78: bulk soft-delete. Single Dexie transaction so partial
  // failure leaves the table consistent (either all rows updated or none).
  // Returns the rows that were soft-deleted so the caller can push them
  // to the server individually (each row has its own server_id).
  async bulkRemove(local_uuids) {
    const ids = Array.isArray(local_uuids) ? local_uuids : Array.from(local_uuids);
    if (ids.length === 0) return [];
    const now = nowIso();
    return db.transaction('rw', db.transactions, async () => {
      const rows = await db.transactions.bulkGet(ids);
      const toUpdate = rows
        .filter(r => r && !r.deleted)
        .map(r => ({ ...r, deleted: true, updated_at: now }));
      if (toUpdate.length > 0) {
        await db.transactions.bulkPut(toUpdate);
      }
      return toUpdate;
    });
  },
  async wipe() {
    await db.transactions.clear();
  },
};

// Phase 155 (M-82): Mockable wrapper around txRepo. Used by
// sampleData.js so tests can inject a fake txRepo without spinning
// up Dexie. Production callers that import txRepo directly bypass
// this and go to Dexie as before.
export const txRepoMockable = makeRepoProxy('TxRepo', txRepo);

// ─── Accounts ───────────────────────────────────────────────────────────
//
// Phase 203: accounts are LOCAL-ONLY on this codebase — the server has
// no /accounts endpoint (probed in Phase 202a, returned 404), and
// pullFromServer.js documents this explicitly. So accounts live purely
// in the local Dexie `accounts` table.
//
// Phase 203 (bug fix): AddTransactionModal was calling
// `data.getAccounts()`, which never existed on REAL_DATA in
// dataService.js — the optional-chaining guard `data.getAccounts ? ...`
// silently swallowed the miss, so the account picker always rendered
// empty and EVERY transaction was silently forced to
// 'default-cash-account'. This repo is the single source of truth for
// account reads; the Settings page (AccountsSection.jsx) keeps using
// dbMockable directly, which is fine because both read the same table.
//
// Sorting mirrors categoryRepo.listAll(): by sort_order, then name.
export const accountRepo = {
  async list() {
    const rows = await db.accounts.toArray();
    return rows
      .filter(a => !a.deleted)
      .sort((a, b) => {
        const ao = a.sort_order ?? 99;
        const bo = b.sort_order ?? 99;
        if (ao !== bo) return ao - bo;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
  },
  async get(local_uuid) { return db.accounts.get(local_uuid); },
};

// ─── Categories ─────────────────────────────────────────────────────────

export const categoryRepo = {
  // Phase 83: listAll() returns every non-deleted category INCLUDING
  // disabled ones. Used by the Settings page so disabled categories
  // stay visible (greyed out + toggle OFF) — the user can re-enable
  // them later. Pickers + filters should keep using list() which
  // hides disabled categories so they don't pollute the Add Transaction
  // modal or filter dropdowns.
  async listAll() {
    const rows = await db.categories.toArray();
    return rows
      .filter(c => !c.deleted)
      .sort((a, b) => {
        if (a.type !== b.type) return a.type < b.type ? -1 : 1;
        return a.sort_order - b.sort_order;
      });
  },
  async list() {
    // Phase 79f: filter soft-deleted + disabled defaults. Disabled
    // categories are hidden from pickers + filter dropdowns.
    const rows = await this.listAll();
    return rows.filter(c => !c.disabled);
  },
  async get(local_uuid) { return db.categories.get(local_uuid); },
  // Phase 168: CRUD via createCrudRepo factory. Phase 79f: user-created
  // categories are never defaults — disabled:false, is_default:false,
  // sort_order:100 baked in here.
  ...createCrudRepo('categories', {
    disabled: false,
    is_default: false,
    sort_order: 100,
  }),
  async reorder(ordered) {
    // ordered: [{ local_uuid, sort_order, type }]
    await db.transaction('rw', db.categories, async () => {
      for (const o of ordered) {
        await db.categories.update(o.local_uuid, { sort_order: o.sort_order, updated_at: nowIso() });
      }
    });
  },
  async remove(local_uuid) {
    await db.categories.update(local_uuid, { deleted: true, updated_at: nowIso() });
  },
  // Phase 79f: toggle a default category's disabled flag. Mirror of the
  // server's PATCH /categories/:id/toggle endpoint. Used by Settings.
  async setDisabled(local_uuid, disabled) {
    await db.categories.update(local_uuid, { disabled: !!disabled, updated_at: nowIso() });
  },
};

// Phase 155 (M-82): Mockable wrapper around categoryRepo. Same
// pattern as txRepoMockable.
export const categoryRepoMockable = makeRepoProxy('CategoryRepo', categoryRepo);

// ─── Settings ──────────────────────────────────────────────────────────
//
// Phase 151 (M-78): test escape hatch — settingsRepo is a Proxy that
// delegates to a test mock (globalThis.__financeAppTestSettingsRepo__)
// when set, otherwise the real Dexie-backed object. Same Proxy pattern
// as dataService.data/writes (M-76) so per-test re-mocking works even
// after the module is already imported. The test factory function
// `setSettingsRepoMock` lives in src/test/data-service-mock.js.
function pickSettingsRepo() {
  return (typeof globalThis !== 'undefined' && globalThis.__financeAppTestSettingsRepo__)
    || REAL_SETTINGS;
}
const REAL_SETTINGS = {
  async getAll() {
    const rows = await db.settings.filter(s => !s.deleted).toArray();
    return Object.fromEntries(rows.map(r => [r.key, r.value]));
  },
  async get(key) {
    const row = await db.settings.where('key').equals(key).first();
    return row && !row.deleted ? row.value : null;
  },
  async set(key, value) {
    const existing = await db.settings.where('key').equals(key).first();
    if (existing) {
      await db.settings.update(existing.local_uuid, { value, updated_at: nowIso() });
    } else {
      await db.settings.add({ local_uuid: uuid(), server_id: null, key, value, deleted: false, updated_at: nowIso() });
    }
  },
};
export const settingsRepo = new Proxy({}, {
  get(_t, key) {
    const target = pickSettingsRepo();
    const v = target[key];
    return typeof v === 'function' ? v.bind(target) : v;
  },
  has(_t, key) { return key in pickSettingsRepo(); },
});

// ─── Budgets ────────────────────────────────────────────────────────────

export const budgetRepo = {
  async list() {
    return db.budgets.filter(b => !b.deleted).toArray();
  },
  async set(category_id, monthly_limit) {
    // Phase 89: when looking up an existing budget row, skip soft-deleted
    // rows. Otherwise a user who Deletes a budget then tries to re-add
    // it ends up UPDATE-ing the deleted row in place (deleted stays true)
    // — the list filter (`!b.deleted`) hides it forever, so the UI looks
    // like the save "didn't take" while the row stays in IDB with
    // deleted=true and a stale amount.
    const existing = await db.budgets.where('category_id').equals(category_id).filter(b => !b.deleted).first();
    if (existing) {
      await db.budgets.update(existing.local_uuid, { monthly_limit, updated_at: nowIso() });
      return db.budgets.get(existing.local_uuid);
    }
    const row = {
      local_uuid: uuid(),
      server_id: null,
      category_id,
      monthly_limit,
      deleted: false,
      updated_at: nowIso(),
    };
    await db.budgets.add(row);
    return row;
  },
  async remove(category_id) {
    const existing = await db.budgets.where('category_id').equals(category_id).first();
    if (existing) await db.budgets.update(existing.local_uuid, { deleted: true, updated_at: nowIso() });
  },
};

// ─── Recurring ──────────────────────────────────────────────────────────

// Phase 109b: in-memory cache to mirror the server's `applyPendingAll`
// per-user 60s gate. Without this, every Dashboard mount would re-walk
// the recurring rules + audit table on every load — cheap, but wasteful
// since the result is identical between calls within a minute.
// Keyed by user (we only ever have user_id=0 in local-first mode, but
// keeping the namespace lets us extend to multi-account later).
const _recurringApplyCache = new Map();

function clearRecurringApplyCache() {
  _recurringApplyCache.clear();
}
// Exported so tests can clear the apply-cache between calls (the
// production gate is 60s; tests want to exercise the audit check
// without waiting a minute between subtests).
// Phase 112 (M-39): also expose a seed-singleton reset so tests that
//   delete + re-open IndexedDB in `beforeEach` can force the next
//   `ensureSeeded()` to actually re-seed (instead of returning the
//   cached, now-stale promise from the previous run).
export const __test__ = {
  clearRecurringApplyCache,
  resetSeedSingleton() { _ensureSeededInFlight = null; },
};

// Phase 208 / card 208-B3 — the two reminder fields on a recurring rule.
//
// `reminder_enabled`   0/1 — does this rule produce a reminder?
// `reminder_days_before` 0..7 — how many days ahead to fire (0 = on the day).
//
// The default is ASYMMETRIC on purpose (phase spec §4.1 / §5.4):
//   - a NEW rule defaults to reminder_enabled = 1  (the user opted in by
//     creating the rule; the GLOBAL `recurring_reminder_enabled` toggle is
//     the master switch),
//   - a rule that predates Phase 208 has no field at all and therefore
//     reads as 0 (off) — we must not silently start nudging every
//     existing rule.
//
// The defaults are supplied EXPLICITLY by `add()` (not inherited from a
// Dexie row default) so the value that reaches the server push is the
// same value that is in IndexedDB.
const REMINDER_DEFAULT_ENABLED = 1;
const REMINDER_DEFAULT_DAYS_BEFORE = 1;
// The fallback for a value that is ABSENT on a row that already exists.
// Deliberately 0 (off): a rule that predates Phase 208 must not start
// producing reminders because someone re-pushed it to the server.
const REMINDER_FALLBACK_ENABLED = 0;
export {
  REMINDER_DEFAULT_ENABLED,
  REMINDER_DEFAULT_DAYS_BEFORE,
  REMINDER_FALLBACK_ENABLED,
};

/** Coerce a reminder_enabled value to 0/1. `undefined` → the default. */
export function normalizeReminderEnabled(value, fallback) {
  if (value === undefined || value === null) return fallback;
  if (value === true || value === 1) return 1;
  if (value === false || value === 0) return 0;
  if (typeof value === 'string') {
    if (value === 'true' || value === '1') return 1;
    if (value === 'false' || value === '0') return 0;
  }
  return fallback;
}

/** Coerce a reminder_days_before value to an integer. `undefined` → the default. */
export function normalizeReminderDaysBefore(value, fallback) {
  if (value === undefined || value === null || value === '') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

// A row read back from IndexedDB may predate the reminder fields.
// Fill them in so every reader (list/get, the reminder scheduler in
// recurringReminders.js, the Settings list) sees a definite value.
function withReminderDefaults(row) {
  if (!row) return row;
  return {
    ...row,
    reminder_enabled: normalizeReminderEnabled(row.reminder_enabled, 0),
    reminder_days_before: normalizeReminderDaysBefore(row.reminder_days_before, REMINDER_DEFAULT_DAYS_BEFORE),
  };
}

export const recurringRepo = {
  async list() {
    const rows = await db.recurring.filter(r => !r.deleted).toArray();
    return rows.map(withReminderDefaults);
  },
  async get(local_uuid) {
    return withReminderDefaults(await db.recurring.get(local_uuid));
  },
  async add(rule) {
    const row = { local_uuid: uuid(), server_id: null, deleted: false, active: 1, updated_at: nowIso(), ...rule };
    // Phase 208-B3: the caller may pass `reminder_enabled: false` or
    // `reminder_days_before: 0` — both are falsy but MEANINGFUL, so we
    // normalise AFTER the spread (a caller-supplied value always wins)
    // and only fall back to the default when the field is absent.
    row.reminder_enabled = normalizeReminderEnabled(row.reminder_enabled, REMINDER_DEFAULT_ENABLED);
    row.reminder_days_before = normalizeReminderDaysBefore(row.reminder_days_before, REMINDER_DEFAULT_DAYS_BEFORE);
    await db.recurring.add(row);
    clearRecurringApplyCache(); // new rule should be picked up immediately
    return row;
  },
  async update(local_uuid, patch) {
    // Phase 208-B3: the reminder fields are part of the rule, so an
    // update must persist them. Dexie's `update` only touches the keys
    // present in the patch — so a caller that omits the reminder fields
    // keeps the stored values (the "don't clobber on partial update"
    // rule the server mirrors for effective_from/effective_to).
    const next = { ...patch };
    // A key that is present but null/undefined means "not supplied" —
    // keep whatever is already stored instead of clobbering it with a
    // default (mirrors the server's effective_from/effective_to rule).
    if (next.reminder_enabled === null || next.reminder_enabled === undefined) {
      delete next.reminder_enabled;
    } else {
      next.reminder_enabled = normalizeReminderEnabled(next.reminder_enabled, REMINDER_DEFAULT_ENABLED);
    }
    if (next.reminder_days_before === null || next.reminder_days_before === undefined || next.reminder_days_before === '') {
      delete next.reminder_days_before;
    } else {
      next.reminder_days_before = normalizeReminderDaysBefore(next.reminder_days_before, REMINDER_DEFAULT_DAYS_BEFORE);
    }
    await db.recurring.update(local_uuid, { ...next, updated_at: nowIso() });
    clearRecurringApplyCache(); // amount/day/category change → re-evaluate
    // Return the updated row. Phase 208-B3: `dataService.writes.updateRecurring`
    // reads this return value to build the server payload — without it the
    // update path never pushed anything to the server at all.
    return withReminderDefaults(await db.recurring.get(local_uuid));
  },
  async remove(local_uuid) {
    await db.recurring.update(local_uuid, { deleted: true, updated_at: nowIso() });
    clearRecurringApplyCache();
  },
  // Phase 109b: auto-apply any pending recurring rules to transactions.
  // Mirrors server/recurring.js applyPending() but runs over IndexedDB.
  // Returns the list of newly-applied rules so the caller can show a
  // banner ("N recurring rules auto-added for this month"). Cache TTL
  // mirrors the server's per-user 60s window so refreshes are cheap.
  // `today` defaults to `new Date()` and is injectable for tests.
  async applyPending(userId = 0, today = new Date()) {
    const cacheKey = `recurring-apply:${userId}`;
    // Cache marks "checked recently, no new applies" — matches server
    // semantics. Returning the previous inserted list would mislead
    // the dashboard banner into re-counting the same applies on every
    // mount. Cached calls return [] (nothing new since last check).
    if (_recurringApplyCache.has(cacheKey)) return [];
    const currentYm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const currentDay = today.getDate();
    const allRules = await db.recurring.filter(r => !r.deleted && r.active === 1).toArray();
    _recurringApplyCache.set(cacheKey, true); // mark "checked", regardless of result
    if (allRules.length === 0) return [];
    const inserted = [];
    // Single Dexie rw-transaction so all inserts are atomic — partial
    // failure leaves the audit + tx tables consistent.
    await db.transaction('rw', db.recurring, db.recurring_audit, db.transactions, db.categories, async () => {
      for (const r of allRules) {
        // Honour effective range (same as server).
        if (r.effective_from && currentYm < r.effective_from) continue;
        if (r.effective_to && currentYm > r.effective_to) continue;
        // Not yet due this month.
        if (r.day_of_month > currentDay) continue;
        // Already applied this (rule, month) — skip.
        const auditKey = [r.local_uuid, currentYm];
        const existing = await db.recurring_audit.get(auditKey);
        if (existing) continue;
        const occurred = `${currentYm}-${String(r.day_of_month).padStart(2, '0')}`;
        const note = r.note ? `${r.note} (recurring)` : '(recurring)';
        // Look up category for the banner label (mirrors server's
        // JOIN to surface category_name). BUGFIX: Dexie.get(null)
        // throws "Invalid argument" — guard against null/undefined
        // since recurring rules may legitimately have category_id: null
        // (user deleted the category, or never set one).
        const cat = r.category_id != null
          ? await db.categories.get(r.category_id)
          : null;
        const txRow = {
          local_uuid: uuid(),
          server_id: null,
          deleted: false,
          type: r.type,
          amount: r.amount,
          category_id: r.category_id,
          account_local_uuid: r.account_local_uuid || null,
          occurred_on: occurred,
          note,
          created_at: nowIso(),
          updated_at: nowIso(),
        };
        await db.transactions.add(txRow);
        await db.recurring_audit.put({
          recurring_local_uuid: r.local_uuid,
          year_month: currentYm,
          transaction_local_uuid: txRow.local_uuid,
          created_at: nowIso(),
        });
        inserted.push({
          recurring_local_uuid: r.local_uuid,
          transaction_local_uuid: txRow.local_uuid,
          category_name: cat ? cat.name : null,
          amount: r.amount,
          type: r.type,
        });
      }
    });
    return inserted;
  },
};

// ─── Credit cards ──────────────────────────────────────────────────────

export const cardRepo = {
  async list() { return db.credit_cards.filter(c => !c.deleted).toArray(); },
  async get(local_uuid) { return db.credit_cards.get(local_uuid); },
  // Phase 168: CRUD via createCrudRepo factory. No repo-specific defaults.
  ...createCrudRepo('credit_cards'),
};

// ─── Installments (Phase 67) ──────────────────────────────────────────
// Credit card installment plans. Schema: { local_uuid, credit_card_local_uuid,
//   total_amount, total_periods, monthly_amount, start_month ('YYYY-MM'),
//   ended, note, category_id|null, server_id, deleted, created_at, updated_at }
// `ended` is set to true when user marks the plan complete manually.
export const installmentsRepo = {
  async list() { return db.installments.filter(p => !p.deleted).toArray(); },
  async listByCard(credit_card_local_uuid) {
    return db.installments
      .filter(p => !p.deleted && p.credit_card_local_uuid === credit_card_local_uuid)
      .toArray();
  },
  async listActive() {
    return db.installments
      .filter(p => !p.deleted && p.ended !== true)
      .toArray();
  },
  // Phase 168: CRUD via createCrudRepo factory. ended:false and
  // created_at:nowIso() are repo-specific defaults. markEnded stays
  // in this object (specialised).
  ...createCrudRepo('installments', { ended: false, created_at: nowIso() }),
  async markEnded(local_uuid) {
    await db.installments.update(local_uuid, { ended: true, updated_at: nowIso() });
  },
};

// Phase 164 (M-91): Mockable wrapper around installmentsRepo.
// Same Proxy pattern as txRepoMockable (Phase 155). Production
// behaviour identical when no test mock is set — the wrapper falls
// through to the real installmentsRepo.
export const installmentsRepoMockable = makeRepoProxy('InstallmentsRepo', installmentsRepo);

// ─── Goals (Phase 48) ─────────────────────────────────────────────────
// Schema: { local_uuid, server_id, name, target_amount, currency,
//           linked_category_id|null, tracking_from|null, current_override|null,
//           deadline|null, icon, color, created_at, updated_at, deleted }
//
// - `linked_category_id` = local_uuid of the category to auto-track.
//   When set, `current_amount` is computed as the sum of expense
//   transactions in that category since `tracking_from` (or all-time
//   if null). Manual override `current_override` is added on top.
// - When `linked_category_id` is null, goal is in pure-manual mode —
//   user types `current_override` and that's the entire `current_amount`.
// - `deadline` is optional ('YYYY-MM-DD'). The UI computes ETA from
//   the user's avg daily savings; pure-manual goals have no ETA.

export const goalRepo = {
  async list() { return db.goals.filter(g => !g.deleted).toArray(); },
  async get(local_uuid) {
    const row = await db.goals.get(local_uuid);
    return row && !row.deleted ? row : null;
  },
  // Phase 168: CRUD via createCrudRepo factory. The 7 nullable/seeded
  // fields (linked_category_id, tracking_from, current_override,
  // deadline, icon='🎯', color='#10b981', currency='$') plus
  // created_at are repo-specific defaults. The list+get methods above
  // remain because they filter soft-deleted rows.
  ...createCrudRepo('goals', {
    linked_category_id: null,
    tracking_from: null,
    current_override: null,
    deadline: null,
    icon: '🎯',
    color: '#10b981',
    currency: '$',
    created_at: nowIso(),
  }),
};

// ─── Sync helpers ──────────────────────────────────────────────────────
// Used by the sync engine (Phase 35e). Lists rows that need pushing to
// the server: either no server_id (new) or updated_at > server's
// updated_at (modified). Filters out hard-deleted rows (those are
// handled separately).

export const syncDirty = {
  async transactions() {
    return db.transactions
      .filter(t => t.server_id === null || t.server_id === undefined)
      .toArray();
  },
  async categories() {
    return db.categories
      .filter(c => c.server_id === null || c.server_id === undefined)
      .toArray();
  },
  async settings() {
    return db.settings
      .filter(s => s.server_id === null || s.server_id === undefined)
      .toArray();
  },
  async budgets() {
    return db.budgets
      .filter(b => b.server_id === null || b.server_id === undefined)
      .toArray();
  },
  async recurring() {
    return db.recurring
      .filter(r => r.server_id === null || r.server_id === undefined)
      .toArray();
  },
  async cards() {
    return db.credit_cards
      .filter(c => c.server_id === null || c.server_id === undefined)
      .toArray();
  },
  async goals() {
    return db.goals
      .filter(g => g.server_id === null || g.server_id === undefined)
      .toArray();
  },
};

// After push returns server IDs, update local rows to store the mapping.
export const syncApplyServerIds = {
  async transactions(mapping) {
    // mapping: [{ local_uuid, server_id }]
    await db.transaction('rw', db.transactions, async () => {
      for (const m of mapping) {
        await db.transactions.update(m.local_uuid, { server_id: m.server_id });
      }
    });
  },
  async categories(mapping) {
    await db.transaction('rw', db.categories, async () => {
      for (const m of mapping) {
        await db.categories.update(m.local_uuid, { server_id: m.server_id });
      }
    });
  },
  async settings(mapping) {
    await db.transaction('rw', db.settings, async () => {
      for (const m of mapping) {
        await db.settings.update(m.local_uuid, { server_id: m.server_id });
      }
    });
  },
  async budgets(mapping) {
    await db.transaction('rw', db.budgets, async () => {
      for (const m of mapping) {
        await db.budgets.update(m.local_uuid, { server_id: m.server_id });
      }
    });
  },
  async recurring(mapping) {
    await db.transaction('rw', db.recurring, async () => {
      for (const m of mapping) {
        await db.recurring.update(m.local_uuid, { server_id: m.server_id });
      }
    });
  },
  async cards(mapping) {
    await db.transaction('rw', db.credit_cards, async () => {
      for (const m of mapping) {
        await db.credit_cards.update(m.local_uuid, { server_id: m.server_id });
      }
    });
  },
  async goals(mapping) {
    await db.transaction('rw', db.goals, async () => {
      for (const m of mapping) {
        await db.goals.update(m.local_uuid, { server_id: m.server_id });
      }
    });
  },
};

// Test-only: hard reset the entire local DB. Used by tests + by the
// "Clear local data" button in Settings (with a confirm).
export async function hardResetLocal() {
  // Phase 105 fix: also clear the module-level seed-singleton promise so
  // the post-reset schema (now empty) actually re-seeds. Without this,
  // the singleton still holds the *first* (now-completed) call's promise
  // and `await ensureSeeded()` returns it without checking catCount —
  // leading to a freshly-opened, empty DB that thinks it's seeded.
  _ensureSeededInFlight = null;
  await db.delete();
  await db.open();
  await ensureSeeded();
}

// Export schema for debugging.
export { FinanceDB as _FinanceDB };